import React, { useState, useEffect, useRef } from 'react';
import { motion as Motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { smoothScrollIntoView } from '../../utils/smoothScroll';
import { CalcCard, CalcIntro, CalcSlider, ResultRow, AnimatedValue } from './components/CalcCard';

import { 
  CheckCircle2, 
  ArrowRight, 
  Zap, 
  Layers, 
  Plus, 
  Minus, 
  ShoppingBag,
  MousePointer2,
  Calendar,
  ShieldCheck,
  TrendingUp,
  Wallet,
  Check,
  Hourglass,
  Building2,
  Home,
  Scale,
  Scissors,
  Car,
  UtensilsCrossed,
  Layout,
  Dumbbell,
  Bed,
  Flower2,
  Stethoscope,
  Smartphone,
  MessageSquare,
  Box,
  Globe,
  BarChart3,
  Bot,
  PhoneCall,
  Headphones,
  ChevronDown,
  Database,
  Rocket,
  Code,
  Target,
  Mic
} from 'lucide-react';
import CampaignLayout from './CampaignLayout';
import CampaignCountdown from './components/CampaignCountdown';
import {
  CropMarks,
  Crosshair,
  FlowChain,
  SystemMarker,
  SectionCounter,
  VerticalLabel,
} from '../../components/swiss/SwissKit';
import TeaserVideo from './components/TeaserVideo';
import SectionHeading from './components/SectionHeading';
import StakesSection from './components/StakesSection';
import HeroAmbientBackground from '../../components/HeroAmbientBackground';
import HeroVideoWall from '../../components/HeroVideoWall';
import HeroBrandMarquee from '../../components/HeroBrandMarquee';
import HeroTapes from '../../components/HeroTapes';
import useHeroWordFit from '../../hooks/useHeroWordFit';

// Rotating hero word; also what useHeroWordFit measures on mobile.
const HERO_WORD_CLASS = 'absolute left-1/2 -translate-x-1/2 top-0 block origin-center whitespace-nowrap uppercase text-[1.08em] md:text-[1.12em] audiowide-regular font-normal';
import { useCart } from '../../hooks/useCart';
import { getFeaturedCampaigns } from '../data/campaigns';

const FAQItem = React.memo(({ item, color }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="border-b border-border-color last:border-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="w-full py-8 flex items-center justify-between text-left group transition-all"
      >
        <h5 
          className="text-xl md:text-2xl font-black tracking-tight flex-1 pr-8 transition-colors duration-300"
          style={{ color: (isOpen || isHovered) ? color : 'inherit' }}
        >
          {item.q}
        </h5>
        <div 
          className="w-10 h-10 rounded-full border border-border-color flex items-center justify-center transition-all duration-500 group-hover:scale-110"
          style={{ borderColor: isOpen ? color : 'inherit', backgroundColor: isOpen ? `${color}10` : 'rgba(255, 255, 255, 0.03)' }}
        >
          {isOpen ? <Minus size={18} style={{ color }} /> : <Plus size={18} style={{ color: isHovered ? color : 'inherit' }} />}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <Motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-8 text-text-muted font-light leading-relaxed max-w-4xl">
              {item.a}
            </p>
          </Motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const SectionCTA = ({ color, lang, onClick, location }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  return (
    <div className="w-full flex justify-center py-16 md:py-24 border-t border-border-color bg-bg-color">
      <Motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => {
          if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'orientation_call_click', { location: location || 'section_bottom' });
          }
          onClick();
        }}
        className="group relative px-8 py-4 md:px-12 md:py-5 font-black uppercase ui-text transition-all duration-500 overflow-hidden bg-text-color text-bg-color rounded-[4px]"
      >
        <div 
          className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500" 
          style={{ backgroundColor: color }}
        />
        <span className="relative z-10 flex items-center transition-colors duration-500 group-hover:text-white">
          {lang === 'de' ? 'Live-Demo buchen' : 'Book Live Demo Call'}
          <span className="w-0 opacity-0 group-hover:w-5 group-hover:ml-3 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
            <ArrowRight size={isMobile ? 16 : 20} />
          </span>
        </span>
      </Motion.button>
    </div>
  );
};

const MODULES = [
  { id: 'A', name: 'Personality', price: 499, icon: <Bot size={20} />, description: 'Custom-tuned archetype reflecting your brand voice.' },
  { id: 'B', name: 'Languages', price: 850, icon: <Globe size={20} />, description: 'Fluent responses in 15+ global languages.' },
  { id: 'C', name: 'CRM Sync', price: 599, icon: <Database size={20} />, description: 'Direct bi-directional data flow with your stack.' },
  { id: 'D', name: 'Sentiment', price: 350, icon: <BarChart3 size={20} />, description: 'Real-time mood detection and adaptive logic.' },
  { id: 'E', name: 'Escalation', price: 450, icon: <PhoneCall size={20} />, description: 'Smart handovers to human agents when needed.' },
  { id: 'F', name: 'Training', price: 950, icon: <ShieldCheck size={20} />, description: 'Deep-knowledge training on your documentation.' }
];

const CAMPAIGNS = getFeaturedCampaigns({ Rocket, Code, Mic, Target }).map((campaign) => {
  const Icon = campaign.icon;

  return {
    ...campaign,
    icon: <Icon size={20} />,
  };
});

/* Same model as the reference calculator: recovered revenue is calls the
   agent now answers that convert, payroll is the share of phone time the agent
   takes over. 250 working days, 70 % of phone time handled by the agent. */
const WORKDAYS_PER_YEAR = 250;
const AGENT_SHARE = 0.7;
const SETUP_FEE = 3999;

const SavingsCalculator = React.memo(({ color, lang }) => {
  const [missedCalls, setMissedCalls] = useState(5);
  const [avgDealValue, setAvgDealValue] = useState(600);
  const [conversion, setConversion] = useState(25);
  const [phoneHours, setPhoneHours] = useState(15);
  const [hourlyCost, setHourlyCost] = useState(25);

  const de = lang === 'de';
  const nf = React.useMemo(() => new Intl.NumberFormat(de ? 'de-DE' : 'en-US', { maximumFractionDigits: 0 }), [de]);
  const money = React.useCallback((n) => (de ? `${nf.format(n)}\u00A0€` : `€${nf.format(n)}`), [de, nf]);

  const { recoveredRevenue, savedPayroll, savedHours, savedDays, totalSaved, roiMultiple } = React.useMemo(() => {
    const recoveredRevenue = Math.round(missedCalls * avgDealValue * (conversion / 100) * WORKDAYS_PER_YEAR);
    const savedHours = Math.round(phoneHours * AGENT_SHARE * 52);
    const savedPayroll = Math.round(savedHours * hourlyCost);
    const savedDays = Math.round(savedHours / 8);
    const totalSaved = recoveredRevenue + savedPayroll;
    // Measured against the smallest tier's first year, setup included.
    const firstYearCost = SETUP_FEE + PLANS[0].monthly * 12;
    const roiMultiple = Math.max(0, Math.round(totalSaved / firstYearCost));
    return { recoveredRevenue, savedPayroll, savedHours, savedDays, totalSaved, roiMultiple };
  }, [missedCalls, avgDealValue, conversion, phoneHours, hourlyCost]);

  const sliders = [
    { label: de ? 'Verpasste Anrufe pro Tag' : 'Missed calls per day', value: missedCalls, set: setMissedCalls, min: 1, max: 50, step: 1, display: missedCalls },
    { label: de ? 'Ø Auftragswert eines Kunden' : 'Avg. value of a customer', value: avgDealValue, set: setAvgDealValue, min: 50, max: 10000, step: 50, display: money(avgDealValue) },
    { label: de ? 'Davon werden Kunden' : 'Share that become customers', value: conversion, set: setConversion, min: 5, max: 100, step: 5, display: de ? `${conversion} %` : `${conversion}%` },
    { label: de ? 'Telefonzeit deines Teams pro Woche' : 'Team phone time per week', value: phoneHours, set: setPhoneHours, min: 1, max: 80, step: 1, display: `${phoneHours} h` },
    { label: de ? 'Stundensatz Mitarbeiter' : 'Hourly staff cost', value: hourlyCost, set: setHourlyCost, min: 10, max: 150, step: 5, display: money(hourlyCost) },
  ];

  return (
    <div className="mt-12 md:mt-24">
      <CalcCard
        color={color}
        className="lg:grid-cols-[1.15fr_1fr]"
        left={(
          <>
            <CalcIntro
              eyebrow={de ? 'Deine Zahlen' : 'Your numbers'}
              title={de ? 'Wie viel bringt dir jeder angenommene Anruf?' : 'What is every answered call worth to you?'}
              text={de
                ? 'Schieb die Regler auf deine Werte. Rechts siehst du sofort, was dein KI-Telefonassistent pro Jahr zurückholt.'
                : 'Move the sliders to your numbers. On the right you instantly see what your AI phone assistant wins back per year.'}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7 md:gap-y-9">
              {sliders.map((s) => (
                <CalcSlider
                  key={s.label}
                  label={s.label}
                  value={s.value}
                  display={s.display}
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  onChange={s.set}
                  color={color}
                />
              ))}
            </div>
          </>
        )}
        right={(
          <div className="flex flex-col h-full">
            <div className="space-y-6">
              <ResultRow
                icon={TrendingUp}
                title={de ? 'Umsatz pro Jahr' : 'Revenue per year'}
                sub={de ? 'Zurückgewonnen' : 'Recovered'}
                value={<AnimatedValue value={recoveredRevenue} format={money} />}
              />
              <ResultRow
                icon={Wallet}
                title={de ? 'Personalkosten pro Jahr' : 'Payroll per year'}
                sub={de ? 'Eingespart' : 'Saved'}
                value={<AnimatedValue value={savedPayroll} format={money} />}
              />
              <ResultRow
                icon={Hourglass}
                title={de ? 'Arbeitszeit pro Jahr' : 'Working time per year'}
                sub={de ? `Freigesetzt, rund ${nf.format(savedDays)} Arbeitstage` : `Freed up, around ${nf.format(savedDays)} working days`}
                value={<AnimatedValue value={savedHours} format={(n) => `${nf.format(n)} h`} />}
              />
            </div>

            <div className="mt-7 pt-7 border-t border-white/25">
              <div className="flex items-baseline gap-3">
                <span className="text-base md:text-lg font-semibold">{de ? 'Dein Potenzial' : 'Your potential'}</span>
                <span className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-white/70">{de ? 'Pro Jahr' : 'Per year'}</span>
              </div>
              <AnimatedValue
                value={totalSaved}
                format={money}
                className="block mt-2 font-black tracking-tight leading-none tabular-nums"
                style={{ fontSize: 'clamp(44px, 4.6vw + 10px, 72px)' }}
              />
              <div className="mt-4 text-sm md:text-[15px] text-white/85 leading-relaxed">
                <strong className="font-black text-white">{roiMultiple}{de ? 'fach' : '×'}</strong>
                {de
                  ? ' zurück, gemessen an den Kosten im ersten Jahr (Starter inklusive Einrichtung).'
                  : ' return, measured against the first-year cost (Starter including setup).'}
              </div>
            </div>

            <div className="mt-7">
              <div className="text-xs text-white/70 leading-relaxed">
                {de
                  ? `Schätzung auf Basis deiner Angaben: ${WORKDAYS_PER_YEAR} Arbeitstage, dein Agent übernimmt rund ${AGENT_SHARE * 100} Prozent der Telefonzeit.`
                  : `Estimate based on your inputs: ${WORKDAYS_PER_YEAR} working days, your agent handles around ${AGENT_SHARE * 100} percent of phone time.`}
              </div>
            </div>
          </div>
        )}
      />
    </div>
  );
});

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    monthly: 79,
    yearly: 795,
    desc: { de: 'Für Einzelunternehmen und kleine Praxen.', en: 'For sole traders and small practices.' },
    features: {
      de: ['1 KI Agent', '200 Gesprächsminuten pro Monat', 'Telefon und Website Chat', 'Terminbuchung im Google Kalender', 'Leads, Gesprächsprotokolle und Bestätigungsmails', 'Website Scan, Automatisierungen und Mailvorlagen'],
      en: ['1 AI agent', '200 call minutes per month', 'Phone and website chat', 'Appointment booking in Google Calendar', 'Leads, call transcripts and confirmation emails', 'Website scan, automations and email templates'],
    },
  },
  {
    id: 'professional',
    name: 'Professional',
    monthly: 199,
    yearly: 1990,
    featured: true,
    desc: { de: 'Für wachsende Unternehmen.', en: 'For growing businesses.' },
    includes: { de: 'Alles aus Starter, plus', en: 'Everything in Starter, plus' },
    features: {
      de: ['5 KI Agenten', '1.000 Gesprächsminuten pro Monat', 'Laufende Optimierung deiner Gesprächsleitfäden', 'Monitoring von Qualität und Gesprächen', 'Prioritätssupport'],
      en: ['5 AI agents', '1,000 call minutes per month', 'Ongoing optimisation of your call scripts', 'Quality and conversation monitoring', 'Priority support'],
    },
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthly: 499,
    yearly: 4990,
    desc: { de: 'Für große Teams und Agenturen.', en: 'For large teams and agencies.' },
    includes: { de: 'Alles aus Professional, plus', en: 'Everything in Professional, plus' },
    features: {
      de: ['10 KI Agenten', '5.000 Gesprächsminuten pro Monat', 'White Label Option', 'Individuelles KI Training'],
      en: ['10 AI agents', '5,000 call minutes per month', 'White label option', 'Custom AI training'],
    },
  },
];

// Warm accent for checks and the "everything in X" eyebrow — same tone as the
// calculator card's eyebrow.
const WARM_ACCENT = '#F0532A';


const TargetGroupItem = React.memo(({ item, i, color, theme }) => {
  const Icon = item.icon;
  const isLight = theme === 'light';
  const ref = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const iconY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <Motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      viewport={{ once: true, amount: 0.1 }}
      className="group p-14 md:p-20 md:pt-14 md:pb-16 border-b border-border-color md:border-r md:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(3n-1)]:border-r lg:[&:nth-child(3n-2)]:border-r lg:[&:nth-child(3n)]:border-r-0 bg-surface-color/30 flex flex-col justify-between relative overflow-hidden gpu-accelerated"
    >
      <Motion.div 
        style={{ y: iconY, translateZ: 0 }}
        className="absolute top-0 right-0 p-10 opacity-[0.02] pointer-events-none block"
      >
        <Icon size={300} strokeWidth={1} color={isLight ? '#000000' : '#ffffff'} />
      </Motion.div>

      <div className="relative z-10">
        <div className="flex justify-between items-start mb-10">
          <div 
            className="w-24 h-24 md:w-20 md:h-20 rounded-full flex items-center justify-center bg-surface-color"
            style={{ color: color }}
          >
            <Icon size={40} strokeWidth={1.5} />
          </div>
        </div>

        <h4 className="font-black leading-none mb-5 tracking-tighter text-text-color">
          {item.title}
        </h4>
        <p className="text-text-muted leading-relaxed font-light mb-8">
          {item.desc}
        </p>

        {item.tags && (
          <div className="flex flex-wrap gap-2 mt-auto">
            {item.tags.map((tag, idx) => (
              <span 
                key={idx} 
                className="text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 border border-border-color rounded-[4px] text-text-muted"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Motion.div>
  );
});

const PricingSection = React.memo(({ color, lang, onCta, theme }) => {
  const de = lang === 'de';
  const nf = React.useMemo(() => new Intl.NumberFormat(de ? 'de-DE' : 'en-US', { maximumFractionDigits: 0 }), [de]);
  const money = (n) => (de ? `${nf.format(n)}\u00A0€` : `€${nf.format(n)}`);
  const ctaLabel = de ? 'Erstgespräch buchen' : 'Book an intro call';

  return (
    <section id="pricing" className="py-20 md:py-32">
      <div className="max-w-[1800px] mx-auto px-6 md:px-20">
        <SectionHeading
          title={de ? 'Transparente' : 'Transparent'}
          highlight={de ? 'Preise' : 'Pricing'}
          color={color}
          label={de ? 'Kein Kleingedrucktes' : 'No Fine Print'}
          description={de
            ? 'Einmalige Einrichtung, danach ein fester Monatspreis. Wähle den Plan, der zu deinem Gesprächsvolumen passt, und wechsle jederzeit.'
            : 'A one-time setup, then a fixed monthly price. Pick the plan that fits your call volume and switch any time.'}
          centered={true}
          theme={theme}
        />

        <div className="mt-12 md:mt-24 w-full font-['Inter',sans-serif]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`rounded-[24px] bg-bg-color p-7 sm:p-9 flex flex-col ${
                  plan.featured ? 'border-2 shadow-[0_30px_60px_-40px_rgba(0,0,0,0.45)]' : 'border border-border-color'
                }`}
                style={plan.featured ? { borderColor: color } : undefined}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xl font-semibold text-text-color">{plan.name}</div>
                  {plan.featured && (
                    <span className="text-[11px] font-semibold px-3 py-1 rounded-full text-white" style={{ backgroundColor: color }}>
                      {de ? 'Beliebt' : 'Popular'}
                    </span>
                  )}
                </div>

                <div className="mt-7 flex items-baseline gap-1.5 flex-wrap">
                  <span className="font-black tracking-tighter leading-none text-text-color" style={{ fontSize: 'clamp(56px, 3.6vw + 24px, 84px)' }}>
                    {money(plan.monthly)}
                  </span>
                  <span className="text-sm text-text-muted">{de ? 'pro Monat' : 'per month'}</span>
                </div>

                <div className="mt-6 text-sm md:text-[15px] font-light text-text-muted leading-relaxed">
                  {plan.desc[lang]} {de ? 'Jährlich' : 'Yearly'} {money(plan.yearly)}.
                </div>

                {plan.includes && (
                  <div className="mt-6 text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: WARM_ACCENT }}>
                    {plan.includes[lang]}
                  </div>
                )}

                <ul className={`${plan.includes ? 'mt-5' : 'mt-6'} space-y-3.5 mb-9`}>
                  {plan.features[lang].map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm md:text-[15px] text-text-color leading-snug">
                      <Check size={16} strokeWidth={2.5} className="shrink-0 mt-0.5" style={{ color: WARM_ACCENT }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={onCta}
                  className={`group mt-auto w-full flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm md:text-[15px] font-semibold transition-transform duration-300 hover:scale-[1.01] ${
                    plan.featured ? 'text-white' : 'border border-border-color text-text-color hover:border-text-color/40'
                  }`}
                  style={plan.featured ? { backgroundColor: color } : undefined}
                >
                  {ctaLabel}
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                    style={plan.featured ? undefined : { color: WARM_ACCENT }}
                  />
                </button>
              </div>
            ))}
          </div>

          {/* One-time setup, shared by every plan. */}
          <div
            className="mt-5 rounded-[24px] px-7 sm:px-9 py-7 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-7 text-white"
            style={{ backgroundColor: color }}
          >
            <span className="font-black tracking-tighter leading-none whitespace-nowrap" style={{ fontSize: 'clamp(40px, 2vw + 24px, 52px)' }}>
              {money(SETUP_FEE)}
            </span>
            <div className="flex-1 text-sm md:text-base text-white/85 leading-relaxed">
              <strong className="font-bold text-white">{de ? 'Einmalige Einrichtung.' : 'One-time setup.'}</strong>{' '}
              {de
                ? 'Unser Team richtet deinen Agenten komplett ein: Wissen, Stimme, Gesprächsleitfaden, Kalender und Nummer. Getestet und startklar.'
                : 'Our team sets up your agent end to end: knowledge, voice, call script, calendar and number. Tested and ready to go.'}
            </div>
            <button
              type="button"
              onClick={onCta}
              className="group shrink-0 self-start lg:self-auto flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm md:text-[15px] font-semibold transition-transform duration-300 hover:scale-[1.02]"
              style={{ color }}
            >
              {ctaLabel}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>

          <div className="mt-5 text-sm font-light text-text-muted">
            {de
              ? 'Zusätzliche Minuten 0,10 €, weitere Agenten 49 € pro Monat, Ortsnummer 15 € pro Monat. Alle Preise zuzüglich Mehrwertsteuer.'
              : 'Additional minutes €0.10, extra agents €49 per month, local number €15 per month. All prices exclude VAT.'}
          </div>
        </div>
      </div>
    </section>
  );
});

// Swiss instrumentation section counter — same decorative role as Rapid
// Prototyping's TOTAL_SECTIONS, matched to this page's actual <section> count.
const TOTAL_SECTIONS = 8;

const AIVoiceAgent = ({ setView, theme, setTheme, lang, setLang }) => {
  const { cartItems, addItem, setIsCartOpen } = useCart();
  const [selectedModules, setSelectedModules] = useState([]);
  const [hoveredModule, setHoveredModule] = useState(null);
  const [wordIndex, setWordIndex] = useState(0);

  // Concierge Voice MP3 speech synth / simulation definitions
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Concierge Voice MP3 speech synth / simulation definitions
  const [transcript, setTranscript] = useState('');
  const [secs, setSecs] = useState(0);
  const [voiceStateText, setVoiceStateText] = useState(lang === 'de' ? 'Demo anhören' : 'Listen to Demo');
  const [showCaret, setShowCaret] = useState(false);
  const [voiceTalking, setVoiceTalking] = useState(false);

  const timeoutsRef = useRef([]);

  const VOICE_CUES = React.useMemo(() => [
    { t: 0.0, text: 'Guten Tag, Restaurant Milano! Wie kann ich Ihnen helfen?' },
    { t: 4.5, text: 'Sehr gerne. Ein Tisch für vier Personen, heute um 19:30 Uhr.' },
    { t: 9.0, text: 'Ich habe Fensterplätze für Sie reserviert. Passt das?' },
    { t: 13.0, text: 'Perfekt, Ihre Reservierung ist bestätigt. Bis heute Abend!' }
  ], []);

  const VOICE_CUES_EN = React.useMemo(() => [
    { t: 0.0, text: 'Good day, Restaurant Milano! How can I help you?' },
    { t: 4.5, text: 'With pleasure. A table for four people, today at 7:30 PM.' },
    { t: 9.0, text: 'I have reserved window seats for you. Does that work?' },
    { t: 13.0, text: 'Perfect, your reservation is confirmed. See you tonight!' }
  ], []);

  const stopVoice = React.useCallback(() => {
    setVoiceTalking(false);
    setTranscript('');
    setShowCaret(false);
    setVoiceStateText(lang === 'de' ? 'Demo anhören' : 'Listen to Demo');
    setSecs(0);
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, [lang]);

  const startVoice = React.useCallback(() => {
    setVoiceTalking(true);
    setShowCaret(true);
    setVoiceStateText('Live');
    setSecs(0);

    const cues = lang === 'de' ? VOICE_CUES : VOICE_CUES_EN;

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      
      cues.forEach((cue, index) => {
        const timeoutId = setTimeout(() => {
          let charIndex = 0;
          const text = cue.text;
          const typeNextChar = () => {
            if (charIndex <= text.length) {
              setTranscript(text.substring(0, charIndex));
              charIndex++;
              const typeTimeout = setTimeout(typeNextChar, 30 + Math.random() * 25);
              timeoutsRef.current.push(typeTimeout);
            }
          };
          typeNextChar();

          const utterance = new SpeechSynthesisUtterance(text);
          utterance.lang = lang === 'de' ? 'de-DE' : 'en-US';
          utterance.rate = 1.0;
          utterance.pitch = 1.0;

          if (lang === 'de') {
            const GermanVoice = window.speechSynthesis.getVoices().find(v => /de[-_]DE/i.test(v.lang) && /female|anna|petra|katja|vicki/i.test(v.name)) || window.speechSynthesis.getVoices().find(v => /^de/i.test(v.lang));
            if (GermanVoice) utterance.voice = GermanVoice;
          } else {
            const EnglishVoice = window.speechSynthesis.getVoices().find(v => /en[-_]US/i.test(v.lang) && /female|samantha|zira|karen/i.test(v.name)) || window.speechSynthesis.getVoices().find(v => /^en/i.test(v.lang));
            if (EnglishVoice) utterance.voice = EnglishVoice;
          }

          window.speechSynthesis.speak(utterance);

          if (index === cues.length - 1) {
            const endTimeout = setTimeout(() => {
              stopVoice();
            }, 5000);
            timeoutsRef.current.push(endTimeout);
          }
        }, cue.t * 1000);

        timeoutsRef.current.push(timeoutId);
      });
    } else {
      cues.forEach((cue, index) => {
        const timeoutId = setTimeout(() => {
          let charIndex = 0;
          const text = cue.text;
          const typeNextChar = () => {
            if (charIndex <= text.length) {
              setTranscript(text.substring(0, charIndex));
              charIndex++;
              const typeTimeout = setTimeout(typeNextChar, 30 + Math.random() * 25);
              timeoutsRef.current.push(typeTimeout);
            }
          };
          typeNextChar();

          if (index === cues.length - 1) {
            const endTimeout = setTimeout(() => {
              stopVoice();
            }, 5000);
            timeoutsRef.current.push(endTimeout);
          }
        }, cue.t * 1000);
        timeoutsRef.current.push(timeoutId);
      });
    }
  }, [lang, VOICE_CUES, VOICE_CUES_EN, stopVoice]);

  useEffect(() => {
    let interval;
    if (voiceTalking) {
      interval = setInterval(() => {
        const randomVibe = 0.35 + Math.abs(Math.sin(Date.now() * 0.005)) * 0.45 + Math.random() * 0.15;
        window.__cre8now_voice_level = randomVibe;
      }, 50);
    } else {
      window.__cre8now_voice_level = 0;
    }
    return () => {
      clearInterval(interval);
      window.__cre8now_voice_level = 0;
    };
  }, [voiceTalking]);

  useEffect(() => {
    let timer;
    if (voiceTalking) {
      timer = setInterval(() => {
        setSecs(prev => prev + 1);
      }, 1000);
    } else {
      setSecs(0);
    }
    return () => clearInterval(timer);
  }, [voiceTalking]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  const TARGET_WORDS = React.useMemo(() => lang === 'de' 
    ? ['Fitness', 'Restaurants', 'Hotels', 'Yoga Studios', 'Autohäuser', 'Arztpraxen', 'Kanzleien', 'Friseure', 'Makler']
    : ['Fitness', 'Restaurants', 'Hotels', 'Yoga Studios', 'Dealerships', 'Doctors', 'Lawyers', 'Hair Salons', 'Real Estate'], [lang]);
  // Mobile: each rotating word grows into the free width (shared hook).
  const { measureBox: wordBox, ...wordFit } = useHeroWordFit(TARGET_WORDS, HERO_WORD_CLASS);


  useEffect(() => {
    const timer = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % TARGET_WORDS.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [TARGET_WORDS.length]);

  const sprintCount = 1;
  const color = theme === 'dark' ? '#4f8ef0' : '#2563eb';
  const existingItem = cartItems.find(item => item.id === 'ai-voice-agent');
  const isInCart = !!existingItem;
  const hasChanged = !existingItem || JSON.stringify([...selectedModules].sort()) !== JSON.stringify([...(existingItem.selectedModules || [])].sort());

  // Sync with cart if item already exists
  useEffect(() => {
    const existing = cartItems.find(item => item.id === 'ai-voice-agent');
    if (existing && existing.selectedModules) {
      setSelectedModules(existing.selectedModules);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hero scroll animations
  const containerRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [50, 550], [0, -150]);
  const heroOpacity = useTransform(scrollY, [50, 400], [1, 0]);
  const heroScale = useTransform(scrollY, [50, 550], [1, 0.9]);

  // Staggered child elements scroll transforms - each moves different distances for parallax effect
  const h4Y = useTransform(scrollY, [70, 570], [0, -150]);
  const h4Opacity = useTransform(scrollY, [70, 420], [1, 0]);
  const headlineY = useTransform(scrollY, [90, 590], [0, -120]);
  const headlineOpacity = useTransform(scrollY, [90, 440], [1, 0]);
  const descY = useTransform(scrollY, [110, 610], [0, -90]);
  const descOpacity = useTransform(scrollY, [110, 460], [1, 0]);
  const buttonY = useTransform(scrollY, [130, 630], [0, -60]);
  const buttonOpacity = useTransform(scrollY, [130, 480], [1, 0]);

  const TRANSLATIONS = {
    de: {
      hero: {
        subtitle: "Der No. 1 KI Voice-Agent für",
        title: "Digital Concierge",
        description: "Verwandle teuren Telefon-Stress in pure Freiheit. CRE8NOW baut intelligente Voice-Agents, die jeden Anruf in Umsatz verwandeln, Termine vollautomatisch buchen und deinem Team die Zeit zurückgeben, die es für echtes Wachstum braucht. Kein verpasster Lead, keine Personalkosten-Falle, nur maximale Skalierung.",
        cta: "Live-Demo buchen",
        marquee: ["24/7 Erreichbarkeit", "Ultra-realistische Stimme", "Automatisches Booking", "Nahtloser CRM-Sync", "15+ Sprachen fließend"]
      },
      stakes: {
        benefitsLabel: "Mit KI Agent",
        benefitsTitle: ["Leads", "Captured"],
        benefitsText: "Jeder Anruf wird sofort beantwortet von einer Stimme, die dein Business kennt. Keine Warteschlangen, keine verpassten Chancen, kein Personalaufwand.",
        benefitsCheckList: ["24/7 Erreichbar", "Kein Lead verloren", "Instant Scale", "CRM Sync", "Kein Headcount"],
        lossesLabel: "Ohne KI Agent",
        lossesTitle: ["Leads", "Burned"],
        lossesText: "Leads erkalten in Minuten. Ein verpasster Anruf ist ein verlorener Kunde. Skalierung über Personalkosten zu finanzieren ist ein Kostentrap.",
        lossesCheckList: ["Verpasste Leads", "Umsatz vernichtet", "Support-Stau", "Hohe Fixkosten", "Kein Wachstum"]
      },
      process: {
        label: "Methodik & Anbindung",
        title: "Dein Weg zu",
        highlight: "Mehr Leads",
        steps: [
          { 
            day: 'Schritt 1', 
            title: 'Wissen & Basis', 
            desc: 'CRE8NOW analysiert deine bestehende Dokumentation, FAQs und Brand Guidelines. Auf dieser Basis definiert CRE8NOW die einzigartige Persönlichkeit deines Agenten: von der Tonalität bis hin zum spezifischen Fachvokabular deiner Branche.'
          },
          { 
            day: 'Schritt 2', 
            title: 'Logik & Ablauf', 
            desc: 'CRE8NOW entwirft intelligente Konversationsbäume, die nicht nur Fragen beantworten, sondern aktiv führen, und implementiert psychologisch optimierte Vertriebsskripte und Einwandbehandlung für maximale Conversion-Rates.' 
          },
          { 
            day: 'Schritt 3', 
            title: 'System & CRM', 
            desc: 'Hier passiert die Magie: CRE8NOW verbindet den Agenten mit HubSpot, Salesforce oder Pipedrive, richtet Webhooks für Echtzeit-Datenübertragung ein und integriert Kalendersysteme wie Calendly oder Google für direkte Terminbuchungen.' 
          },
          { 
            day: 'Schritt 4', 
            title: 'Training & Stimme', 
            desc: 'CRE8NOW wählt das optimale Large Language Model (LLM) und trainiert es auf deine spezifischen Anwendungsfälle. CRE8NOW integriert absolut menschlich klingende Stimmen aus der Premium-Library für ein natürliches Gesprächserlebnis.' 
          },
          { 
            day: 'Schritt 5', 
            title: 'Stress & Test', 
            desc: 'Vor dem Go-Live simuliert CRE8NOW hunderte von Anrufszenarien und testet das Handling von Hintergrundgeräuschen, Dialekten und komplexen Rückfragen, um eine Fehlerquote von nahezu Null zu garantieren.' 
          },
          { 
            day: 'Schritt 6', 
            title: 'Live & Start', 
            desc: 'Dein Agent geht live auf deinen Telefonnummern. Du erhältst Zugriff auf ein Echtzeit-Dashboard mit Transkripten, Analysen und ROI-Metriken. CRE8NOW überwacht die ersten 48 Stunden intensiv für maximale Stabilität.' 
          }
        ]
      },
      integrations: {
        label: "Anbindung",
        title: "Nahtlose",
        highlight: "Integration",
        desc: "CRE8NOW verbindet deinen Voice Agent mit den Systemen, die du bereits nutzt: von globalen CRMs bis hin zu individuellen Webhooks.",
        crm: "HubSpot, Salesforce, Pipedrive Sync",
        cal: "Live-Terminbuchung & Kalender-Sync",
        api: "Automatisierte Workflows via Webhooks"
      },
      configurator: {
        label: "Der Deal",
        title: "Erstelle deinen",
        highlight: "Booster",
        step1: "Kern Sprint",
        coreTitle: "Autonomer KI Voice-Agent",
        coreDesc: "Vollständige Implementierung inklusive individueller Persönlichkeit, Aufgabenlogik und API-Setup.",
        step2: "Erweiterungsmodule",
        summary: "Bestellübersicht",
        total: "Gesamtgebühr",
        depositLabel: "Fällig (50%)",
        depositDesc: "Deine 50% Anzahlung sichert deinen Slot. Die restlichen 50% sind nach Abnahme fällig.",
        cta: "Deal sichern"
      },
      faqItems: [
        {
          q: "Wie läuft der Onboarding-Prozess ab?",
          a: "Nach deiner Buchung startet CRE8NOW mit einer Strategie-Session, in der Markenstimme, Ziele und technischen Anforderungen genau definiert werden. Danach übernimmt CRE8NOW das komplette Setup: den Bau der KI-Logik, die CRM-Anbindung und den Feinschliff an der Stimme. Innerhalb weniger Tage erhältst du einen funktionierenden Prototypen zum Testen."
        },
        {
          q: "Kann der Voice Agent Anrufe an mein menschliches Team weiterleiten?",
          a: "Ja. Der KI-Agent erkennt automatisch, wenn ein Anrufer menschliche Hilfe benötigt, ein komplexes Problem auftritt oder der Anrufer explizit nach einem Mitarbeiter fragt. Er leitet den Anruf nahtlos weiter und sendet sofort das bisherige Transkript an dein Team."
        },
        {
          q: "Lässt sich der KI-Agent in meine aktuelle Software integrieren?",
          a: "Absolut. CRE8NOW baut maßgeschneiderte API-Verbindungen, um die KI mit deinen bestehenden Tools zu verknüpfen, egal ob HubSpot, Salesforce, Calendly oder eigene Webhooks. So kann die KI beispielsweise direkt Termine in deinen Kalender eintragen oder Kundendaten ins CRM übertragen."
        },
        {
          q: "Welche Sprachen werden unterstützt?",
          a: "Unsere KI Voice Agenten verstehen und sprechen fließend über 15 Hauptsprachen, darunter Deutsch, Englisch, Spanisch, Französisch, Italienisch und viele mehr. Sie können sogar die Sprache des Anrufers erkennen und in Echtzeit automatisch wechseln."
        },
        {
          q: "Wie sicher sind die Daten während eines Anrufs?",
          a: "CRE8NOW hält sich strikt an die DSGVO-Richtlinien. Alle Transkripte und Anrufdaten werden verschlüsselt und sicher übertragen. Die Daten werden ausschließlich für deine Geschäftsprozesse verarbeitet und niemals zum Training öffentlicher KI-Modelle verwendet."
        }
      ]
    },
    en: {
      hero: {
        subtitle: "The No. 1 AI Voice Agent for",
        title: "Digital Concierge",
        description: "Turn expensive phone stress into absolute freedom. CRE8NOW builds intelligent voice agents that convert every call into revenue, book appointments automatically, and give your team back the time they need for real growth. No missed leads, no staffing traps, just pure scalability.",
        cta: "Book Live Demo Call",
        marquee: ["24/7 Live Availability", "Ultra-Realistic Voice", "Automated Booking", "Seamless CRM Sync", "15+ Languages Fluent"]
      },
      stakes: {
        benefitsLabel: "With AI Agent",
        benefitsTitle: ["Leads", "Captured"],
        benefitsText: "Every call answered instantly by a voice that knows your business inside-out. No queues, no missed opportunities, no headcount.",
        benefitsCheckList: ["24/7 Available", "Zero Missed Leads", "Instant Scale", "CRM Sync", "No Headcount"],
        lossesLabel: "Without AI Agent",
        lossesTitle: ["Leads", "Burned"],
        lossesText: "Leads go cold in minutes. Missing a call means losing a customer. Scaling through headcount is a cost trap.",
        lossesCheckList: ["Missed Leads", "Revenue Burned", "Support Lag", "High Payroll", "Zero Scale"]
      },
      process: {
        label: "Methodology & Connect",
        title: "Your Path",
        highlight: "To Leads",
        steps: [
          { 
            day: 'Step 1', 
            title: 'Knowledge & Base', 
            desc: 'CRE8NOW ingests your existing documentation, FAQs, and brand guidelines, then defines your agent’s unique personality: from its tone of voice to industry-specific vocabulary.'
          },
          { 
            day: 'Step 2', 
            title: 'Logic & Flow', 
            desc: 'CRE8NOW designs intelligent conversation trees that don’t just answer questions but actively guide the caller, and implements psychologically optimized sales scripts and objection handling for maximum conversion.' 
          },
          { 
            day: 'Step 3', 
            title: 'System & CRM', 
            desc: 'Where the magic happens: CRE8NOW connects your agent to HubSpot, Salesforce, or Pipedrive, sets up real-time webhooks, and integrates scheduling systems like Calendly or Google for direct bookings.' 
          },
          { 
            day: 'Step 4', 
            title: 'Training & Voice', 
            desc: 'CRE8NOW selects the optimal Large Language Model (LLM) and fine-tunes it for your use cases, and integrates human-like premium voices from its library for a completely natural conversational experience.' 
          },
          { 
            day: 'Step 5', 
            title: 'Stress & Test', 
            desc: 'Before go-live, CRE8NOW simulates hundreds of call scenarios and tests background noise handling, dialects, and complex follow-up questions to guarantee an error rate near zero.' 
          },
          { 
            day: 'Step 6', 
            title: 'Live & Launch', 
            desc: 'Your agent goes live on your phone numbers. You get access to a real-time dashboard with transcripts, analytics, and ROI metrics. CRE8NOW monitors the first 48 hours intensively for maximum stability.' 
          }
        ]
      },
      integrations: {
        label: "Connectivity",
        title: "Seamless",
        highlight: "Integration",
        desc: "CRE8NOW connects your voice agent to the systems you already use: from global CRMs to custom proprietary webhooks.",
        crm: "HubSpot, Salesforce, Pipedrive Sync",
        cal: "Live Scheduling & Calendar Sync",
        api: "Automated Workflows via Webhooks"
      },
      configurator: {
        label: "The Deal",
        title: "Build your",
        highlight: "Booster",
        step1: "Core Sprint",
        coreTitle: "Autonomous AI Voice Agent",
        coreDesc: "Full deployment including custom personality, complex task logic, and API setup.",
        step2: "Expansion Modules",
        summary: "Order Summary",
        total: "Total Fee",
        depositLabel: "Due (50%)",
        depositDesc: "Your 50% deposit secures your slot. Final 50% due upon approval.",
        cta: "Secure Deal"
      },
      faqItems: [
        {
          q: "What does the onboarding process look like?",
          a: "After you book, CRE8NOW kicks off with a strategy session that dives deep into your brand voice, goals, and technical requirements. From there, CRE8NOW handles the entire setup: building the AI logic, connecting to your CRM, and fine-tuning the voice. Within days, you’ll receive a functional prototype to test."
        },
        {
          q: "Can the Voice Agent transfer calls to my human team?",
          a: "Yes. The AI agent can automatically detect when a caller needs human assistance, when a complex issue arises, or if the caller explicitly requests to speak to a person. It will seamlessly transfer the call and immediately send the transcript to your team."
        },
        {
          q: "Does the AI Agent integrate with my current software?",
          a: "Absolutely. CRE8NOW builds custom API connections to link the AI with your existing tools, whether it's HubSpot, Salesforce, Calendly, or custom webhooks. This means the AI can book appointments directly into your calendar or log customer data straight into your CRM."
        },
        {
          q: "What languages are supported?",
          a: "Our AI Voice Agents can fluidly understand and speak over 15 major languages, including English, German, Spanish, French, Italian, and more. It can even detect the caller's language and switch automatically in real-time."
        },
        {
          q: "How secure is the data handled during a call?",
          a: "CRE8NOW adheres strictly to GDPR data privacy regulations. All transcripts and call data are encrypted and transmitted securely. The data is processed solely for your business operations and is never used to train public LLM models."
        }
      ]
    }
  };

  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;

  const NAV_ITEMS = React.useMemo(() => [
    { id: 'process', label: lang === 'de' ? 'Methodik' : 'Process' },
    { id: 'target-groups', label: lang === 'de' ? 'Branchen' : 'Industries' },
    { id: 'calculator', label: lang === 'de' ? 'Kalkulator' : 'Calculator' },
    { id: 'pricing', label: lang === 'de' ? 'Preise' : 'Pricing' },
    { id: 'faq', label: 'FAQ' }
  ], [lang]);

  const MODULES_LOCALIZED = React.useMemo(() => [
    { id: 'A', name: lang === 'de' ? 'Voice-Persönlichkeit' : 'Voice Personality', price: 499, days: 2, icon: <Bot size={20} />, description: lang === 'de' ? 'Individuell abgestimmter Archetyp, der deine Markenstimme widerspiegelt.' : 'Custom-tuned archetype reflecting your brand voice.' },
    { id: 'B', name: lang === 'de' ? 'Global Pack' : 'Global Pack', price: 850, days: 3, icon: <Globe size={20} />, description: lang === 'de' ? 'Fließende Antworten in vielen Sprachen.' : 'Fluent responses in many global languages.' },
    { id: 'C', name: lang === 'de' ? 'CRM Sync' : 'CRM Sync', price: 599, days: 3, icon: <Database size={20} />, description: lang === 'de' ? 'Direkter bidirektioaler Datenfluss mit deinem Stack.' : 'Direct bi-directional data flow with your stack.' },
    { id: 'D', name: lang === 'de' ? 'Mood Check' : 'Mood Check', price: 350, days: 1, icon: <BarChart3 size={20} />, description: lang === 'de' ? 'Echtzeit-Stimmungserkennung und adaptive Logik.' : 'Real-time mood detection and adaptive logic.' },
    { id: 'E', name: lang === 'de' ? 'Handover' : 'Handover', price: 450, days: 2, icon: <PhoneCall size={20} />, description: lang === 'de' ? 'Intelligente Übergabe an menschliche Agenten.' : 'Smart handovers to human agents when needed.' },
    { id: 'F', name: lang === 'de' ? 'Expert Training' : 'Expert Training', price: 950, days: 4, icon: <ShieldCheck size={20} />, description: lang === 'de' ? 'Tiefgreifendes Wissenstraining auf Basis deiner Daten.' : 'Deep-knowledge training on your documentation.' }
  ], [lang]);

  const handleAddToCart = () => {    if (isInCart && !hasChanged) {
      setIsCartOpen(true);
      return;
    }    addItem({
      id: 'ai-voice-agent',
      title: 'Voice Agent',
      tagline: 'Automated front desk.',
      sprintCount,
      selectedModules,
      moduleDetails: MODULES_LOCALIZED.reduce((acc, m) => ({ ...acc, [m.id]: { name: m.name, price: m.price, days: m.days } }), {}),
      totals,
      color
    });
  };

  const toggleModule = (id) => {
    setSelectedModules(prev => 
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const basePrice = 3499;
  const baseDays = 10;
  const subtotalBase = sprintCount * basePrice;
  const subtotalBaseDays = sprintCount * baseDays;
  const subtotalModules = selectedModules.reduce((sum, id) => {
    const module = MODULES_LOCALIZED.find(m => m.id === id);
    return sum + (module ? module.price : 0);
  }, 0);
  const subtotalModuleDays = selectedModules.reduce((sum, id) => {
    const module = MODULES_LOCALIZED.find(m => m.id === id);
    return sum + (module ? module.days : 0);
  }, 0);
  
  const bundleDiscount = sprintCount >= 2 ? subtotalModules * 0.15 : 0;
  const grandTotal = subtotalBase + subtotalModules - bundleDiscount;
  const grandTotalDays = subtotalBaseDays + subtotalModuleDays;
  const deposit = grandTotal * 0.5;

  const totals = { subtotalBase, subtotalModules, bundleDiscount, grandTotal, deposit, grandTotalDays, baseDays };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: typeof window !== 'undefined' && window.innerWidth < 768 ? 20 : 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
  };

  return (
    <div className="w-full">
      {/* HERO SECTION */}
      {/* Dark-mode background behind the video wall, fixed regardless of the
          light/dark toggle — same identity as the Design/AI/Prototyping
          heroes, white ink with yellow micrographic marks. Uses the AI clips
          until Concierge has its own. */}
      <section className="relative min-h-[100dvh] w-full flex items-center justify-center gpu-accelerated hero-section-container" style={{ backgroundColor: 'var(--cre8-night)' }}>
        <HeroVideoWall mode="ai" tint="var(--cre8-night)" ink="#ffffff" />

        <div className="hidden md:block absolute inset-0 z-10 pointer-events-none" style={{ color: '#ffffff' }}>
          <CropMarks insetTop={8} insetX={8} insetBottom={8} color="var(--cre8-yellow)" />
          <div className="absolute" style={{ top: '38%', left: 24 }}>
            <Crosshair size={12} color="var(--cre8-yellow)" />
          </div>
          <div className="absolute bottom-20 md:bottom-24 left-6">
            <FlowChain steps={['CALL', 'AI AGENT', 'BOOKING']} color="var(--cre8-yellow)" />
          </div>
          <div className="flex absolute bottom-20 md:bottom-24 right-6 items-center gap-3">
            <SystemMarker label="CRE8NOW / VOICE" color="var(--cre8-yellow)" />
            <SectionCounter current={1} total={TOTAL_SECTIONS} />
          </div>
          <VerticalLabel text="CRE8NOW / SYSTEM" side="left" offset={24} />
          <VerticalLabel text="MODE / CONCIERGE" side="right" offset={24} />
        </div>

        <div className="hero-stack relative z-10 w-full px-6 md:px-[8vw] max-w-5xl mx-auto flex flex-col items-center text-center pointer-events-none" style={{ color: '#ffffff' }}>
          <Motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="flex flex-col items-center text-center pointer-events-auto"
          >
            <Motion.h4
              variants={fadeInUp}
              exit={{ opacity: 0, y: 20 }}
              className="hero-h4 block mb-1 leading-[0.95] uppercase tracking-tight [text-shadow:none]"
              style={{ y: h4Y, opacity: h4Opacity }}
            >
              {t.hero.subtitle}
            </Motion.h4>
            <Motion.h1
              variants={fadeInUp}
              exit={{ opacity: 0, y: 20 }}
              className="font-black leading-[0.85] tracking-tight uppercase w-full mt-4"
              style={{ y: headlineY, opacity: headlineOpacity }}
            >
              <Motion.span
                ref={wordBox}
                className="relative inline-block h-[1.1em] md:h-[1em] w-full align-top"
                style={{ perspective: '1000px', ...(wordFit.active && { height: wordFit.boxHeight }) }}
              >
                <AnimatePresence mode="wait">
                  <Motion.span 
                    key={wordIndex}
                    initial={{ scale: 1.2, opacity: 0, filter: 'blur(10px)' }}
                    animate={{ 
                      scale: 1, 
                      opacity: 1, 
                      filter: 'blur(0px)',
                      transition: { duration: 1.5, ease: [0.22, 1, 0.36, 1] } 
                    }}
                    exit={{ 
                      scale: 0.8, 
                      opacity: 0, 
                      filter: 'blur(10px)',
                      transition: { duration: 1, ease: [0.64, 0, 0.78, 0] } 
                    }}
                    className={HERO_WORD_CLASS}
                    style={{ color: 'var(--cre8-yellow)', ...(wordFit.active && { fontSize: `${1.08 * wordFit.fitFor(TARGET_WORDS[wordIndex])}em`, top: '50%', translate: '-50% -50%' }) }}
                  >
                    {TARGET_WORDS[wordIndex]}
                  </Motion.span>
                </AnimatePresence>
                {/* Invisible spacer to maintain layout height */}
                <span className="max-md:hidden invisible opacity-0 pointer-events-none whitespace-nowrap uppercase px-4 text-[1.08em] md:text-[1.12em] audiowide-regular font-normal" aria-hidden="true">
                  {TARGET_WORDS[0]}
                </span>
              </Motion.span>
            </Motion.h1>
            
            <Motion.p
              variants={fadeInUp}
              exit={{ opacity: 0, y: 20 }}
              className="max-w-[1200px] w-[90vw] hero-copy mx-auto leading-relaxed font-light mb-8 -mt-0.5 md:-mt-3 gpu-accelerated text-center"
              style={{ y: descY, opacity: descOpacity, color: 'rgba(255,255,255,0.8)' }}
            >
              {t.hero.description}
            </Motion.p>

            <Motion.div
              initial={{ opacity: 0, scale: 1.2 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ delay: 0.8 }}
              className="w-full flex justify-center"
              style={{ y: buttonY, opacity: buttonOpacity }}
            >
              <button
                onClick={() => {
                  if (typeof window !== 'undefined' && window.gtag) {
                    window.gtag('event', 'orientation_call_click', { location: 'hero' });
                  }
                  window.dispatchEvent(new CustomEvent('open_concierge_modal'));
                }}
                style={{ backgroundColor: '#ffffff', color: 'var(--cre8-blue)' }}
                className="hero-cta group relative px-[2.4em] py-[1.1em] font-black uppercase ui-text transition-all duration-500 overflow-hidden rounded-[4px]"
              >
                <div className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500" style={{ backgroundColor: 'var(--cre8-ink)' }} />
                <span className="relative z-10 flex items-center group-hover:text-white transition-colors duration-500">
                  {t.hero.cta}
                  <span className="w-0 opacity-0 group-hover:w-[1.25em] group-hover:ml-[0.75em] group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                    <ArrowRight size="1.1em" />
                  </span>
                </span>
              </button>
            </Motion.div>
          </Motion.div>
        </div>

        {/* USP marquee along the hero floor — same auto-scrolling construction
            as the Rapid Prototyping hero, replacing the static wrapped-bullets
            row this used to end on. Two duplicated sets feed
            animate-infinite-scroll-fast so the loop never shows a seam; the
            second set is aria-hidden since it's a visual duplicate. */}
        <Motion.div
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="absolute bottom-0 left-0 right-0 hero-marquee-container z-10 w-full overflow-hidden"
        >
          <HeroBrandMarquee bg="var(--hero-marquee-bg)" />
        </Motion.div>
      </section>

      {/* TEASER VIDEO */}
      <section className="mt-[20vw] md:mt-[6vw] py-[1vw] bg-surface-color/5">
        <div className="max-w-[1800px] mx-auto px-6 md:px-20 flex flex-col items-center">
            <TeaserVideo src="/assets/cre8now-videos/cre8now-digital-concierge.mp4" />
        </div>
      </section>
      <HeroTapes items={t.hero.marquee} accent="var(--cre8-yellow)" accentInk="var(--cre8-ink)" />

      {/* THE STAKES - HIDDEN */}
      {false && <div id="stakes">
        <StakesSection t={t.stakes} color={color} />
      </div>}

      {/* SPRINT PROCESS - EXPANDED & COMBINED */}
      <section id="process" className="py-20 md:py-32 bg-surface-color/5">
        <div className="max-w-[1800px] mx-auto px-6 md:px-20">
          <SectionHeading 
            title={t.process.title}
            highlight={t.process.highlight}
            color={color}
            label={t.process.label}
            description={lang === 'de' 
              ? 'Von der ersten Datenanalyse bis zur vollautomatisierten CRM-Synchronisierung: Unser 6-Schritte-Prozess garantiert maximale Performance, operative Freiheit und eine nahtlose Integration in deinen bestehenden Workflow.' 
              : 'From initial data analysis to full CRM synchronization: Our 6-step process guarantees maximum performance, operational freedom, and seamless integration into your existing workflow.'}
            centered={true}
          />

          <div className="relative mt-12 md:mt-24">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
              {t.process.steps.map((step, idx) => (
                <Motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true }}
                  className="group relative"
                >
                  <div className="h-full p-10 border border-border-color rounded-[4px] bg-surface-color/50 backdrop-blur-sm relative z-10 hover:border-pink-500/30 transition-all duration-500">
                    <div className="flex items-center justify-between mb-8">
                      <div className="text-[10px] font-black uppercase tracking-[0.3em] flex items-center gap-3" style={{ color }}>
                        {step.day.toUpperCase()}
                      </div>
                      <div 
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ 
                          backgroundColor: color,
                          boxShadow: `0 0 15px ${color}`
                        }}
                      />
                    </div>
                    
                    <h4 className="text-xl font-black mb-5 tracking-tighter">{step.title}</h4>
                    <p className="text-sm font-light leading-relaxed text-text-muted">{step.desc}</p>
                  </div>
                </Motion.div>
              ))}
            </div>
          </div>

          <SectionCTA 
            color={color} 
            lang={lang} 
            onClick={() => window.dispatchEvent(new CustomEvent('open_concierge_modal'))} 
            location="campaign_process_bottom" 
          />
        </div>
      </section>

      {/* TARGET GROUPS */}
      <section id="target-groups" className="py-20 md:py-32 overflow-hidden">
        <div className="w-full">
          <div className="max-w-[1800px] mx-auto px-6 md:px-20">
            <SectionHeading
              title={lang === 'de' ? 'Sprachassistenten für' : 'Voice agents for'}
              highlight={lang === 'de' ? 'Dein Business' : 'your Business'}
              color={color}
              label={lang === 'de' ? 'Für wen es gebaut ist' : 'Built for you'}
              description={lang === 'de' 
                ? 'Erlebe spürbar weniger Stress, gewinne konstant mehr qualifizierte Leads und steigere nachhaltig deine Umsätze. Unsere maßgeschneiderten KI-Sprachassistenten amortisieren sich in Rekordzeit und sichern dir einen unschlagbaren Return on Investment (ROI) für die Zukunft deines Unternehmens.' 
                : 'Experience noticeably less stress, capture more high-quality leads consistently, and drive sustainable revenue growth. Our custom AI voice agents pay for themselves in record time, securing an unbeatable Return on Investment (ROI) for the future of your business.'}
              centered={true}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-border-color mt-12 md:mt-16 w-full">
            {[
              {
                icon: Dumbbell,
                title: lang === 'de' ? 'Fitnessstudios' : 'Fitness Studios',
                desc: lang === 'de' ? 'Onboarding und Mitgliedschaft-Support automatisiert. Dein Agent klärt Fragen zu Verträgen und Kursen, während dein Team auf der Fläche glänzt.' : 'Automated onboarding and membership support. Your agent handles contract queries and class info while your team focuses on the floor.',
                tags: lang === 'de' 
                  ? ["Mitglieder-Onboarding", "Vertrags-Auskunft", "Kurs-Buchung", "Lead-Generierung", "24/7 Support", "Beitragswesen"] 
                  : ["Member Onboarding", "Contract Info", "Class Booking", "Lead Generation", "24/7 Support", "Billing Queries"]
              },
              {
                icon: UtensilsCrossed,
                title: lang === 'de' ? 'Restaurants' : 'Restaurants',
                desc: lang === 'de' ? 'Reservierungen und Menü-Anfragen ohne Personalaufwand. Der Agent bucht Tische direkt in dein System und managed Stoßzeiten souverän.' : 'Reservations and menu inquiries without the staffing overhead. The agent books tables directly into your system and handles peak hours with ease.',
                tags: lang === 'de' 
                  ? ["Tisch-Reservierung", "Menü-Auskunft", "Stoßzeiten-Management", "Allergene-Info", "Event-Anfragen", "Stornierungen"] 
                  : ["Table Booking", "Menu Info", "Peak Hour Support", "Allergen Details", "Event Inquiries", "Cancellations"]
              },
              {
                icon: Bed,
                title: lang === 'de' ? 'Hotels' : 'Hotels',
                desc: lang === 'de' ? 'Dein virtueller Concierge für Buchungen und Gäste-Anfragen. Entlastet die Rezeption und bietet internationalen Gästen Support in 15+ Sprachen.' : 'Your virtual concierge for bookings and guest inquiries. Relieves the front desk and provides support to international guests in 15+ languages.',
                tags: lang === 'de' 
                  ? ["Check-In Info", "Gäste-Anfragen", "15+ Sprachen", "Zimmer-Upgrades", "Local-Guide", "Late-Checkout"] 
                  : ["Check-In Info", "Guest Requests", "15+ Languages", "Room Upgrades", "Local Guide", "Late Checkout"]
              },
              {
                icon: Flower2,
                title: lang === 'de' ? 'Yoga Studios' : 'Yoga Studios',
                desc: lang === 'de' ? 'Kursbuchungen und Retreat-Anfragen weich und professionell abgewickelt. Kein verpasster Lead mehr während deiner Sessions.' : 'Handle class bookings and retreat inquiries softly and professionally. Never miss a lead during your sessions again.',
                tags: lang === 'de' 
                  ? ["Klassen-Buchung", "Retreat-Anfragen", "Lead-Sicherung", "Stundenplan-Auskunft", "Workshops", "Mitgliedschaften"] 
                  : ["Class Booking", "Retreat Inquiries", "Lead Capture", "Schedule Info", "Workshops", "Memberships"]
              },
              {
                icon: Car,
                title: lang === 'de' ? 'Autohäuser' : 'Car Dealerships',
                desc: lang === 'de' ? 'Probefahrten und Werkstatt-Termine automatisiert vereinbaren. Qualifiziert Leads vor und sorgt für eine lückenlose Erreichbarkeit im Sales.' : 'Automate test drives and service appointments. Qualifies leads upfront and ensures seamless availability in sales.',
                tags: lang === 'de' 
                  ? ["Probefahrten", "Termin-Absprache", "Lead-Qualifizierung", "Modell-Auskunft", "Service-Annahme", "Angebote"] 
                  : ["Test Drives", "Service Booking", "Lead Qualification", "Model Details", "Service Intake", "Quotes"]
              },
              {
                icon: Stethoscope,
                title: lang === 'de' ? 'Arztpraxen' : 'Medical Practices',
                desc: lang === 'de' ? 'Patienten-Triage und Terminmanagement rund um die Uhr. Entlastet das Praxispersonal und sorgt für eine effiziente Patientenaufnahme.' : '24/7 patient triage and appointment management. Relieves medical staff and ensures efficient patient intake.',
                tags: lang === 'de' 
                  ? ["Patienten-Triage", "Termin-Vergabe", "Entlastung Rezeption", "Rezept-Bestellung", "Sprechzeiten", "Notfall-Weiterleitung"] 
                  : ["Patient Triage", "Appointment Booking", "Staff Relief", "Prescription Orders", "Opening Hours", "Emergency Routing"]
              },
              {
                icon: Scale,
                title: lang === 'de' ? 'Rechtsanwälte' : 'Lawyers & Firms',
                desc: lang === 'de' ? 'Diskrete Mandantenaufnahme und Erstqualifizierung von Anfragen. Bucht Erstgespräche direkt in den Kalender, ohne wertvolle Zeit zu binden.' : 'Discreet client intake and initial lead qualification. Books consultations directly into the calendar without tying up billable hours.',
                tags: lang === 'de' 
                  ? ["Mandantenaufnahme", "Erstqualifizierung", "Kalender-Sync", "Konflikt-Check", "Dokumenten-Anforderung", "Gebühren-Info"] 
                  : ["Client Intake", "Lead Qualification", "Calendar Sync", "Conflict Check", "Document Requests", "Fee Structure"]
              },
              {
                icon: Scissors,
                title: lang === 'de' ? 'Friseursalons' : 'Hair Salons',
                desc: lang === 'de' ? 'Termine koordinieren und Services erklären. Dein Agent managed den Kalender perfekt, während du dich voll auf das Handwerk konzentrierst.' : 'Coordinate appointments and explain services. Your agent manages the calendar perfectly while you focus entirely on your craft.',
                tags: lang === 'de' 
                  ? ["Termin-Koordination", "Service-Erklärung", "Kalender-Pflege", "Stylisten-Wahl", "Preisauskunft", "Produkt-Verkauf"] 
                  : ["Appointment Booking", "Service Details", "Calendar Care", "Stylist Choice", "Price Inquiry", "Product Sales"]
              },
              {
                icon: Home,
                title: lang === 'de' ? 'Makler' : 'Real Estate',
                desc: lang === 'de' ? 'Besichtigungstermine rund um die Uhr buchen und Interessenten vorfiltern. Maximale Velocity für deine Abschlüsse ohne Telefon-Stress.' : 'Book viewing appointments 24/7 and pre-filter potential buyers. Maximum velocity for your closings without the phone stress.',
                tags: lang === 'de' 
                  ? ["Besichtigungen", "Interessenten-Filter", "24/7 Erreichbarkeit", "Objekt-Auskunft", "Suchprofile", "Wertermittlung"] 
                  : ["Showings", "Lead Filtering", "24/7 Availability", "Property Info", "Search Profiles", "Valuation Requests"]
              },
            ].map((group, i) => (
              <TargetGroupItem 
                key={i} 
                item={group} 
                i={i} 
                color={color} 
                theme={theme} 
              />
            ))}
          </div>

          <SectionCTA 
            color={color} 
            lang={lang} 
            onClick={() => window.dispatchEvent(new CustomEvent('open_concierge_modal'))} 
            location="campaign_target_groups_bottom" 
          />
        </div>
      </section>

      {/* SAVINGS CALCULATOR */}
      <section id="calculator" className="py-20 md:py-32 bg-surface-color/5">
        <div className="max-w-[1800px] mx-auto px-6 md:px-20">
          <SectionHeading
            title={lang === 'de' ? 'Dein Potenzial' : 'Your Potential'}
            highlight={lang === 'de' ? 'Kalkulator' : 'Calculator'}
            color={color}
            label={lang === 'de' ? 'Was du zurückgewinnen kannst' : 'What you can recover'}
            description={lang === 'de' 
              ? 'Berechne sofort dein ROI-Potenzial. Unser Tool zeigt dir basierend auf realen Benchmarks, wie viel Umsatz und wertvolle Zeit ein KI Voice Agent für dein Business freisetzt.' 
              : 'Instantly calculate your ROI potential. Based on real-world benchmarks, our tool visualizes exactly how much revenue and valuable time an AI Voice Agent unlocks for your business.'}
            centered={true}
          />
          <SavingsCalculator color={color} lang={lang} />

          <SectionCTA 
            color={color} 
            lang={lang} 
            onClick={() => window.dispatchEvent(new CustomEvent('open_concierge_modal'))} 
            location="campaign_calculator_bottom" 
          />
        </div>
      </section>

      {/* PRICING OVERVIEW */}
      <div id="pricing">
        <PricingSection
          color={color}
          lang={lang}
          onCta={() => {
            if (typeof window !== 'undefined' && window.gtag) {
              window.gtag('event', 'orientation_call_click', { location: 'campaign_pricing_section' });
            }
            window.dispatchEvent(new CustomEvent('open_concierge_modal'));
          }}
          theme={theme}
        />
      </div>

      {/* FAQ SECTION */}
      <section id="faq" className="py-20 md:py-32 border-t border-border-color bg-bg-color">
        <div className="max-w-[1200px] mx-auto px-6 md:px-20">
          <SectionHeading
            title={lang === 'de' ? 'Häufige' : 'Frequently Asked'}
            highlight={lang === 'de' ? 'Fragen' : 'Questions'}
            color={color}
            label="FAQ"
            centered={true}
          />
          <div className="mt-16 border-t border-border-color/30">
            {t.faqItems && t.faqItems.length > 0 ? (
              t.faqItems.map((faq, i) => (
                <FAQItem key={i} item={faq} color={color} />
              ))
            ) : null}
          </div>

          <SectionCTA 
            color={color} 
            lang={lang} 
            onClick={() => window.dispatchEvent(new CustomEvent('open_concierge_modal'))} 
            location="campaign_faq_bottom" 
          />
        </div>
      </section>

      {/* CONFIGURATOR — HIDDEN */}
      {false && <section id="configurator" className="py-[40vw] md:py-[8vw] border-t border-border-color bg-surface-color/5">
        <div className="max-w-[1800px] mx-auto px-6 md:px-20">
          <SectionHeading 
            title={t.configurator.title}
            highlight={t.configurator.highlight}
            color={color}
            label={t.configurator.label}
            centered={true}
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-8 space-y-12">
              <div className="space-y-6">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-text-muted">{t.configurator.step1}</p>
                <div 
                  className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8 border p-6 sm:p-8 rounded-[4px] transition-all duration-500"
                  style={{ 
                    borderColor: color,
                    backgroundColor: `${color}0D`
                  }}
                >
                  <div className="flex-1">
                    <h4 className="text-xl font-black mb-1">{t.configurator.coreTitle}</h4>
                    <p className="text-sm text-text-muted font-light">{t.configurator.coreDesc}</p>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-6 sm:gap-8 border-t sm:border-t-0 border-border-color/30 pt-6 sm:pt-0">
                    <div className="flex flex-col items-end">
                      <div className="text-xl font-mono font-black">
                        €{(3499).toLocaleString()}
                      </div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-text-muted">
                        {totals.baseDays} {lang === 'de' ? 'Tage' : 'Days'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-text-muted">{t.configurator.step2}</p>
                <div className="flex flex-col gap-4">
                  {MODULES_LOCALIZED.map((module) => (
                    <div 
                      key={module.id}
                      onClick={() => toggleModule(module.id)}
                      onMouseEnter={() => setHoveredModule(module.id)}
                      onMouseLeave={() => setHoveredModule(null)}
                      className="group cursor-pointer border p-6 sm:p-8 rounded-[4px] transition-all duration-500 flex flex-col sm:flex-row sm:items-center gap-6"
                      style={{ 
                        borderColor: (selectedModules.includes(module.id) || hoveredModule === module.id) ? color : 'var(--border-color)',
                        backgroundColor: selectedModules.includes(module.id) ? `${color}0D` : 'rgba(255, 255, 255, 0.03)'
                      }}
                    >
                      <div 
                        className="hidden md:flex w-16 h-16 shrink-0 items-center justify-center rounded-[4px] transition-colors"
                        style={{ 
                          backgroundColor: selectedModules.includes(module.id) ? color : 'var(--surface-color)',
                          color: selectedModules.includes(module.id) ? 'white' : 'var(--text-muted)'
                        }}
                      >
                        {selectedModules.includes(module.id) ? (
                          <CheckCircle2 size={24} color="white" />
                        ) : (
                          module.icon && React.cloneElement(module.icon, { size: 24 })
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-black text-xl mb-1 tracking-tight">{module.name}</h4>
                        <p className="text-sm text-text-muted font-light leading-relaxed">{module.description}</p>
                      </div>
                      <div className="text-xl font-mono font-bold pt-4 sm:pt-0 border-t sm:border-t-0 border-border-color/30 flex justify-between sm:flex-col sm:items-end">
                        <span className="sm:hidden text-[10px] text-text-muted uppercase font-bold tracking-widest">Price</span>
                        <div className="flex flex-col items-end">
                          <div>+€{module.price.toLocaleString()}</div>
                          <div 
                            className="mt-1 px-2 py-0.5 rounded-[2px] text-[10px] font-black uppercase tracking-widest border"
                            style={{ 
                              backgroundColor: `${color}20`, 
                              borderColor: `${color}40`,
                              color: color 
                            }}
                          >
                            +{module.days} {lang === 'de' ? 'TAGE' : 'DAYS'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="sticky top-[120px] bg-surface-color/50 backdrop-blur-md border border-border-color rounded-[2px] p-6 space-y-6">
                <h4 className="text-xl font-black tracking-tight">{t.configurator.summary}</h4>
                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-text-muted font-light">{t.configurator.coreTitle}</span>
                    <span className="font-mono font-black">€{totals.subtotalBase.toLocaleString()}</span>
                  </div>
                  {selectedModules.map(moduleId => {
                    const module = MODULES_LOCALIZED.find(m => m.id === moduleId);
                    return (
                      <div key={moduleId} className="flex justify-between text-xs">
                        <span className="text-text-muted font-light">{module.name}</span>
                        <span className="font-mono font-black">€{module.price.toLocaleString()}</span>
                      </div>
                    );
                  })}
                  <div className="pt-4 border-t border-border-color flex justify-between items-end">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-black uppercase tracking-tight mb-1.5 opacity-60">{t.configurator.total}</span>
                      <div 
                        className="flex items-center gap-1.5 px-2 py-0.5 rounded-[2px] w-fit border border-white/10"
                        style={{ backgroundColor: color }}
                      >
                        <span className="text-[9px] font-black uppercase tracking-widest text-white">
                          {totals.grandTotalDays} {lang === 'de' ? 'Tage' : 'Days'}
                        </span>
                      </div>
                    </div>
                    <span className="text-3xl font-mono font-black">€{totals.grandTotal.toLocaleString()}</span>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-dotted border-border-color">
                  <div 
                    className="flex justify-between items-center p-4 rounded-[2px] border"
                    style={{ backgroundColor: color, borderColor: `${color}4D` }}
                  >
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-widest mb-1 text-white/70">{t.configurator.depositLabel}</p>
                      <p className="text-2xl font-mono font-black text-white">€{totals.deposit.toLocaleString()}</p>
                    </div>
                    <ShieldCheck className="text-white/80" size={28} />
                  </div>
                  <p className="text-[9px] text-text-muted font-light leading-relaxed text-center px-2">
                    {t.configurator.depositDesc}
                  </p>
                </div>

                <button 
                  onClick={handleAddToCart}
                  className="group w-full py-5 text-white font-black uppercase text-sm tracking-[0.2em] rounded-[2px] hover:scale-[1.01] transition-all duration-300 flex items-center justify-center gap-2"
                  style={{ backgroundColor: color }}
                >
                  <span className="flex items-center">
                    {isInCart ? 
                      (hasChanged ? 
                        (lang === 'de' ? 'Warenkorb aktualisieren' : 'Update Cart') : 
                        (lang === 'de' ? 'Warenkorb öffnen' : 'Open Cart')
                      ) : t.configurator.cta
                    }
                    <span className="w-0 opacity-0 group-hover:w-5 group-hover:ml-3 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                      <ArrowRight size={18} />
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>}
    </div>
  );
};

export default AIVoiceAgent;

